import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import MainLayout from "./components/MainLayout";
import PublicLayout from "./components/PublicLayout";
import { useTenant } from "./hooks/useTenant";
import { getAllowedMenuIds, getHomePath, getLandingKey } from "./config/tenants";

// Import Pages
import AppLandingPage from "./pages/AppLandingPage";
import AnalystLandingPage from "./pages/AnalystLandingPage";
import TraderBroHomepage from "./pages/TraderBroHomepage";
import LoginPage from "./pages/LoginPage";
import ChatPage from "./pages/ChatPage";
import HermesPage from "./pages/HermesPage";
import FlowPage from "./pages/FlowPage";
import TechnicalChartPage from "./pages/TechnicalChartPage";
import StockScreenerPage from "./pages/StockScreenerPage";
import ContentMonitoringPage from "./pages/ContentMonitoringPage";
import CommunityPage from "./pages/CommunityPage";
import HistoryPage from "./pages/HistoryPage";
import UpgradePage from "./pages/UpgradePage";
import AnalystUpgradePage from "./pages/AnalystUpgradePage";
import CheckoutPage from "./pages/CheckoutPage";
import SettingsPage from "./pages/SettingsPage";
import WatchlistPage from "./pages/WatchlistPage";
import AlertPage from "./pages/AlertPage";
import PortfolioPage from "./pages/PortfolioPage";
import ResearchHubPage from "./pages/ResearchHubPage";
import ResearchArticlePage from "./pages/ResearchArticlePage";
import AnalystsHubPage from "./pages/AnalystsHubPage";
import AnalystProfilePage from "./pages/AnalystProfilePage";
import PredictionDetailPage from "./pages/PredictionDetailPage";
import SymbolAnalystPage from "./pages/SymbolAnalystPage";
import PredictionsPage from "./pages/PredictionsPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import SectorMapPage from "./pages/SectorMapPage";
import SymbolsPage from "./pages/SymbolsPage";
import AnalystFollowPage from "./pages/AnalystFollowPage";
import DemoClosingPage from "./pages/DemoClosingPage";

// Wraps any page in PublicLayout for guest access
function withPublicLayout(Page) {
  return function PublicWrapper() {
    return <PublicLayout><Page /></PublicLayout>;
  };
}

// Renders PrivatePage inside MainLayout when authenticated, PublicPage otherwise
function AuthAwareRoute({ PrivatePage, PublicPage }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
      </div>
    );
  }
  if (!isAuthenticated) return <PublicPage />;
  return <MainLayout><PrivatePage /></MainLayout>;
}

// RequireAuth Component
function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading TraderBro...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function AppContent() {
  const { isAuthenticated } = useAuth();
  const { subdomain } = useTenant();
  const allowedIds = getAllowedMenuIds(subdomain);

  const homePath = getHomePath(subdomain);

  const LANDING_PAGES = {
    home: TraderBroHomepage,
    app: AppLandingPage,
    analysts: AnalystLandingPage,
  };
  const LandingComponent = LANDING_PAGES[getLandingKey(subdomain)] ?? TraderBroHomepage;

  // Redirect authenticated users from login to home
  const PublicRoute = ({ children }) => {
    if (isAuthenticated) {
      return <Navigate to={homePath} replace />;
    }
    return children;
  };

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <LandingComponent />
            </PublicRoute>
          }
        />
        <Route
          path="/auth"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="/checkout" element={<CheckoutPage />} />

        {/* Standalone demo/recording pages — no layout, no auth */}
        <Route path="/demo-closing" element={<DemoClosingPage />} />

        {/* These pages are accessible to everyone — private page shown to all, PublicLayout for guests */}
        <Route path="/predictions" element={<AuthAwareRoute PrivatePage={PredictionsPage} PublicPage={withPublicLayout(PredictionsPage)} />} />
        <Route path="/leaderboard" element={<AuthAwareRoute PrivatePage={LeaderboardPage} PublicPage={withPublicLayout(LeaderboardPage)} />} />
        <Route path="/symbols" element={<AuthAwareRoute PrivatePage={SymbolsPage} PublicPage={withPublicLayout(SymbolsPage)} />} />
        <Route path="/analysts/symbols/:exchange/:ticker" element={<AuthAwareRoute PrivatePage={SymbolAnalystPage} PublicPage={withPublicLayout(SymbolAnalystPage)} />} />
        <Route path="/analysts/predictions/:id" element={<AuthAwareRoute PrivatePage={PredictionDetailPage} PublicPage={withPublicLayout(PredictionDetailPage)} />} />
        <Route path="/analysts/:slug" element={<AuthAwareRoute PrivatePage={AnalystProfilePage} PublicPage={withPublicLayout(AnalystProfilePage)} />} />

        {/* AgentBro (brand) — engine is Hermes internally (HermesPage component,
            /api/agent/hermes/session). Public URL is /agentbro. */}
        <Route path="/agentbro" element={<MainLayout><HermesPage /></MainLayout>} />
        <Route path="/agentbro/:convId" element={<MainLayout><HermesPage /></MainLayout>} />

        {/* Public Research Routes */}
        <Route path="/research" element={<ResearchHubPage />} />
        <Route path="/research/:category/:slug" element={<ResearchArticlePage />} />

        {/* Country-prefixed Research Routes */}
        <Route path="/:country/research" element={<ResearchHubPage />} />
        <Route path="/:country/research/:category/:slug" element={<ResearchArticlePage />} />

        {/* Protected Routes */}
        <Route element={<RequireAuth><MainLayout /></RequireAuth>}>
          {/* Always-visible routes (not menu-gated) */}
          <Route path="/upgrade" element={subdomain === 'app' ? <UpgradePage /> : <AnalystUpgradePage />} />
          <Route path="/settings" element={<SettingsPage />} />

          {/* Tenant-gated routes */}
          {allowedIds.includes('chat') && <Route path="/chat" element={<ChatPage />} />}
          {allowedIds.includes('chart') && <Route path="/chart" element={<TechnicalChartPage />} />}
          {allowedIds.includes('screener') && <Route path="/screener" element={<StockScreenerPage />} />}
          {allowedIds.includes('history') && <Route path="/history" element={<HistoryPage />} />}
          {allowedIds.includes('watchlist') && <Route path="/watchlist" element={<WatchlistPage />} />}
          {allowedIds.includes('following') && <Route path="/following" element={<AnalystFollowPage />} />}
          {allowedIds.includes('portfolio') && <Route path="/portfolio" element={<PortfolioPage />} />}
          {allowedIds.includes('alerts') && <Route path="/alerts" element={<AlertPage />} />}
          {allowedIds.includes('research') && <Route path="/research-area" element={<ResearchHubPage />} />}
          {/* Analysts hub — only for tenants with the analysts menu item */}
          {allowedIds.includes('analysts') && <Route path="/analysts" element={<AnalystsHubPage />} />}
          {allowedIds.includes('sector-map') && <Route path="/sector-map" element={<SectorMapPage />} />}

          {/* Legacy/unused routes — kept registered but not in menu */}
          <Route path="/monitoring" element={<ContentMonitoringPage />} />
          <Route path="/flow" element={<FlowPage />} />
          <Route path="/community" element={<CommunityPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to={isAuthenticated ? homePath : "/"} replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
